import { CoreImageView, CoreTextView } from '@core/widgets';
import { useBehavior } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import clsx from 'clsx';
import React, { Activity } from 'react';
import { getSemanticColors, SemanticColor, TColorVarsMap, toColorVars } from '../../theme/colors';
import { TStatusTrackerData } from '../../types/data';
import { StatusTrackerEnum } from '../../types/enums';
import { TextStyle } from '../../types/styles';
import { getInlineStyles } from '../../utils/styles';
import { getHref } from '../Image/imageUtils';
import { getVisualIconMap } from '../Image/styles/visualUtils';
import styles from './styles/statusTracker.module.css';

/** Local variables of `statusTracker.module.css`, filled from the semantic tokens of the widget's design version. */
const STATUS_TRACKER_COLOR_VARS = {
    '--status-tracker-line': SemanticColor.IconPrimaryDisabled,
    '--status-tracker-accent': SemanticColor.IconEmphasised,
    '--status-tracker-bg': SemanticColor.BgPrimary,
} as const satisfies TColorVarsMap;

export const StatusTracker = ({ widget }: TWidgetProps<TStatusTrackerData>) => {
    const { isHidden, isDisabled } = useBehavior(widget);
    const semanticColors = getSemanticColors(widget.version);
    const visualIconMap = getVisualIconMap(semanticColors);

    const getStatusIcon = (status: StatusTrackerEnum) => {
        let iconName = '';
        const iconHierarchy = status === StatusTrackerEnum.WARNING || status === StatusTrackerEnum.FAILED ? 'destructive' : 'emphasised';

        if (status === StatusTrackerEnum.WAITING) {
            iconName = 'ic_wait_fill';
        } else if (status === StatusTrackerEnum.SUCCESS) {
            iconName = 'ic_checkmark_fill';
        } else if (status === StatusTrackerEnum.WARNING) {
            iconName = 'ic_info_fill';
        } else if (status === StatusTrackerEnum.FAILED) {
            iconName = 'ic_x_fill';
        }

        if (iconName) {
            return (
                <span data-test={`${widget.uid}_icon`} className={styles.iconWrapper}>
                    <CoreImageView src={getHref(iconName, widget.version)} isDisabled={isDisabled} styleProps={{ ...visualIconMap.simple[iconHierarchy] }} />
                </span>
            );
        }
        return null;
    };

    const hasStatusWithIcon = widget.data?.items?.find((item) => getStatusIcon(item?.state) !== null);

    if (Array.isArray(widget.data?.items) && widget.data?.items.length > 0) {
        return (
            <Activity mode={isHidden ? 'hidden' : 'visible'}>
                <div
                    data-test={widget.uid}
                    style={{ ...getInlineStyles(widget.options), ...toColorVars(STATUS_TRACKER_COLOR_VARS, widget.version) }}
                    className={clsx(styles.wrapper, hasStatusWithIcon && styles.wrapperWithIcon)}
                >
                    {widget.data.items.map((item, index) => (
                        <div key={`${item.state}-${index}`} data-test={`${widget.uid}_item`} className={clsx(styles.item, styles[item.state])}>
                            {getStatusIcon(item.state)}
                            <div className={styles.itemContent}>
                                {item.title && (
                                    <CoreTextView
                                        as="p"
                                        uid={`${widget.uid}_title`}
                                        text={item.title}
                                        isDisabled={isDisabled}
                                        styleProps={{
                                            ...TextStyle.main,
                                            main: semanticColors[
                                                item.state === StatusTrackerEnum.INITIAL ? SemanticColor.TextSecondary : SemanticColor.TextPrimary
                                            ],
                                            disabled: { main: semanticColors[SemanticColor.TextPrimaryDisabled] },
                                        }}
                                    />
                                )}
                                {item.subtitle && (
                                    <CoreTextView
                                        as="p"
                                        uid={`${widget.uid}_subtitle`}
                                        text={item.subtitle}
                                        isDisabled={isDisabled}
                                        // Figma StatusTracker: the date sits on text/primary/disabled even while the step is active
                                        styleProps={{
                                            ...TextStyle.secondary,
                                            main: semanticColors[SemanticColor.TextPrimaryDisabled],
                                            disabled: { main: semanticColors[SemanticColor.TextPrimaryDisabled] },
                                            margin: { top: 4 },
                                        }}
                                    />
                                )}
                                {Array.isArray(item.descriptionLines) && item.descriptionLines.length > 0 && (
                                    <div data-test={`${widget.uid}_descriptions`} className={styles.descriptions}>
                                        {item.descriptionLines.map((description, descriptionIndex) => (
                                            <CoreTextView
                                                key={`description-${index}-${descriptionIndex}`}
                                                as="p"
                                                text={description}
                                                isDisabled={isDisabled}
                                                styleProps={{
                                                    ...TextStyle.main,
                                                    main: semanticColors[SemanticColor.TextSecondary],
                                                    disabled: { main: semanticColors[SemanticColor.TextPrimaryDisabled] },
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </Activity>
        );
    }
    return false;
};
